import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Repositories } from '@core/db';
import type { Case, Client } from '@core/domain/model';
import { toListItem, type ClientListItem } from './formatter';
import { telemetry } from '@core/telemetry';

export interface ClientsState {
  isLoading: boolean;
  query: string;
  clients: ClientListItem[];
}

/**
 * Данные списка клиентов.
 *
 * Поиск идёт **по имени, телефону и почте** — «Структура» §7.1: клиента ищут по тому,
 * что вспомнилось первым, а вспоминается чаще номер, чем полное имя.
 */
export function useClients(db: Repositories) {
  const [query, setQuery] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    let dropped = false;

    void (async () => {
      const [allClients, allCases] = await Promise.all([db.clients.list(), db.cases.list({})]);
      if (dropped) return;

      setClients(allClients);
      setCases(allCases);
      setLoading(false);
    })();

    return () => {
      dropped = true;
    };
  }, [db, version]);

  const state: ClientsState = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = (client: Client) =>
      needle.length === 0 ||
      [client.fullName, client.phone, client.email]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(needle));

    return {
      isLoading,
      query,
      clients: clients
        .filter(matches)
        .map((client) =>
          toListItem(
            client,
            cases.filter((item) => item.clientId === client.id),
          ),
        ),
    };
  }, [clients, cases, query, isLoading]);

  const actions = useMemo(
    () => ({
      setQuery,

      /**
       * Заводит клиента. Обязательное поле одно — имя: остальное дописывают потом,
       * ровно как у дела и задачи.
       */
      async createClient(draft: {
        fullName: string;
        phone: string | null;
        email: string | null;
      }) {
        const fullName = draft.fullName.trim();
        if (fullName.length === 0) return null;

        const created = await db.clients.create({
          id: '',
          fullName,
          phone: draft.phone?.trim() || null,
          email: draft.email?.trim() || null,
          birthDate: null,
          address: null,
          notes: null,
        });
        if (created.ok) telemetry.record('clientCreated');
        refresh();

        return created;
      },

      refresh,
    }),
    [db, refresh],
  );

  return { state, actions };
}

export type ClientsActions = ReturnType<typeof useClients>['actions'];
